import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, FileDown, FileSpreadsheet, ChevronDown, ChevronRight, Users } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { rupiah, fmtDate } from "@/lib/format";
import { AgingBadge, InvStatus, BUCKET_LABEL } from "@/components/finance/aging";

const BUCKETS = ["current", "1-30", "31-60", "61-90", "90+"];

function CustomerRow({ g }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className={`ar-rep-row ${g.overdue > 0 ? "overdue" : ""}`} onClick={() => setOpen(!open)} data-testid={`ar-report-row-${g.customer_odoo_id}`}>
        <td>{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td><strong>{g.customer}</strong><small className="fin-muted">{g.area || "-"} · {g.ar_term_name}</small></td>
        <td>{g.invoice_count}</td>
        <td>{rupiah(g.amount_total)}</td>
        <td>{rupiah(g.amount_paid)}</td>
        <td><b>{rupiah(g.outstanding)}</b></td>
        {BUCKETS.map((b) => <td key={b} className={g.buckets[b] > 0 && b !== "current" ? "ar-bad" : ""}>{g.buckets[b] ? rupiah(g.buckets[b]) : "—"}</td>)}
      </tr>
      {open && (
        <tr className="ar-rep-detail"><td /><td colSpan={10}>
          <table className="ar-rep-inner" data-testid={`ar-report-detail-${g.customer_odoo_id}`}>
            <thead><tr><th>Invoice</th><th>SO</th><th>Tgl</th><th>Jatuh Tempo</th><th>Total</th><th>Dibayar</th><th>Sisa</th><th>Umur</th><th>Status</th></tr></thead>
            <tbody>{g.invoices.map((r) => (
              <tr key={r.id}><td>{r.number}</td><td>{r.so_number}</td><td>{fmtDate(r.invoice_date)}</td><td>{fmtDate(r.due_date)}</td>
                <td>{rupiah(r.amount_total)}</td><td>{rupiah(r.amount_paid)}</td><td><b>{rupiah(r.amount_residual)}</b></td>
                <td><AgingBadge inv={r} /></td><td><InvStatus status={r.status} /></td></tr>
            ))}</tbody>
          </table>
        </td></tr>
      )}
    </>
  );
}

export function ArReportTab({ refreshKey }) {
  const [rep, setRep] = useState(null);
  const [q, setQ] = useState("");
  const [includePaid, setIncludePaid] = useState(false);
  const [busy, setBusy] = useState(null);
  const params = { q: q || undefined, include_paid: includePaid };

  const load = useCallback(() => {
    api.get("/finance/ar-report", { params: { q: q || undefined, include_paid: includePaid } })
      .then((r) => setRep(r.data.data)).catch((e) => { toast.error(apiError(e)); setRep({ customers: [], totals: null }); });
  }, [q, includePaid]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);

  const download = async (ext) => {
    setBusy(ext);
    try {
      const r = await api.get(`/finance/ar-report.${ext}`, { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `laporan-ar-customer-${rep?.as_of || "hari-ini"}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(ext === "pdf" ? "PDF diunduh" : "Excel diunduh");
    } catch (e) { toast.error("Gagal mengunduh laporan"); } finally { setBusy(null); }
  };

  const t = rep?.totals;
  return (
    <div className="panel table-panel" data-testid="ar-report-tab">
      <div className="table-tools">
        <div className="search-box"><Search /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari customer…" data-testid="ar-report-search" /></div>
        <label className="ar-rep-check"><input type="checkbox" checked={includePaid} onChange={(e) => setIncludePaid(e.target.checked)} data-testid="ar-report-include-paid" /> Sertakan invoice lunas</label>
        <div className="ar-rep-actions">
          <button className="rj-btn rj-btn-ghost rj-btn-sm" onClick={() => download("pdf")} disabled={!!busy} data-testid="ar-report-pdf-btn">
            {busy === "pdf" ? <Loader2 size={14} className="spin" /> : <FileDown size={14} />} PDF</button>
          <button className="rj-btn rj-btn-green rj-btn-sm" onClick={() => download("xlsx")} disabled={!!busy} data-testid="ar-report-xlsx-btn">
            {busy === "xlsx" ? <Loader2 size={14} className="spin" /> : <FileSpreadsheet size={14} />} Excel</button>
        </div>
      </div>
      {t && (
        <div className="ar-rep-summary" data-testid="ar-report-totals">
          <span><b>{t.customers}</b> customer · <b>{t.invoice_count}</b> invoice</span>
          <span>Sisa piutang <b data-testid="ar-report-outstanding">{rupiah(t.outstanding)}</b></span>
          <span className={t.overdue > 0 ? "ar-bad" : ""}>Lewat jatuh tempo <b data-testid="ar-report-overdue">{rupiah(t.overdue)}</b></span>
          <small className="fin-muted">Per {fmtDate(rep.as_of)}</small>
        </div>
      )}
      {rep === null ? <div className="rj-loading"><Loader2 className="spin" /> Memuat laporan…</div> : rep.customers.length === 0 ? (
        <div className="rj-empty" data-testid="ar-report-empty"><Users /><p>Tidak ada piutang untuk filter ini.</p></div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="fin-table ar-rep-table">
            <thead><tr><th /><th>Customer</th><th>Inv</th><th>Total Tagihan</th><th>Dibayar</th><th>Sisa</th>
              {BUCKETS.map((b) => <th key={b}>{BUCKET_LABEL[b]}</th>)}</tr></thead>
            <tbody>{rep.customers.map((g) => <CustomerRow key={g.customer_odoo_id} g={g} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
